import React, { useMemo, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
export function LoanCalculator() {
  const [principal, setPrincipal] = useState<number>(500000);
  const [rate, setRate] = useState<number>(5.5);
  const [years, setYears] = useState<number>(30);
  const results = useMemo(() => {
    const p = principal || 0;
    const r = (rate || 0) / 100 / 12;
    const n = (years || 0) * 12;
    if (p === 0 || r === 0 || n === 0) {
      return {
        monthlyPayment: 0,
        totalInterest: 0,
        totalCost: 0,
        chartData: []
      };
    }
    const monthlyPayment = p * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    const totalCost = monthlyPayment * n;
    const totalInterest = totalCost - p;
    // Generate chart data (yearly)
    const chartData = [];
    let balance = p;
    let cumulativeInterest = 0;
    for (let year = 0; year <= years; year++) {
      if (year === 0) {
        chartData.push({
          year,
          balance: Math.round(balance),
          interest: 0
        });
        continue;
      }
      for (let month = 1; month <= 12; month++) {
        const interestPayment = balance * r;
        const principalPayment = monthlyPayment - interestPayment;
        balance -= principalPayment;
        cumulativeInterest += interestPayment;
      }
      chartData.push({
        year,
        balance: Math.max(0, Math.round(balance)),
        interest: Math.round(cumulativeInterest)
      });
    }
    return {
      monthlyPayment,
      totalInterest,
      totalCost,
      chartData
    };
  }, [principal, rate, years]);
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0
  }).format(val);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-267">
      {/* Inputs */}
      <div className="lg:col-span-1 space-y-6 bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-268">
        <h3 className="text-xl font-semibold text-white mb-4" data-id="element-269">Loan Details</h3>

        <div data-id="element-270">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-271">
            Loan Amount ($)
          </label>
          <input type="number" value={principal} onChange={e => setPrincipal(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-272" />
        </div>

        <div data-id="element-273">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-274">
            Interest Rate (%)
          </label>
          <input type="number" step="0.1" value={rate} onChange={e => setRate(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-275" />
        </div>

        <div data-id="element-276">
          <label className="block text-sm font-medium text-slate-400 mb-2" data-id="element-277">
            Loan Term (Years)
          </label>
          <input type="number" value={years} onChange={e => setYears(Number(e.target.value))} className="w-full bg-[#0a0a1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" data-id="element-278" />
        </div>
      </div>

      {/* Outputs & Chart */}
      <div className="lg:col-span-2 space-y-6" data-id="element-279">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-280">
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-281">
            <p className="text-sm text-slate-400 mb-1" data-id="element-282">Monthly Repayment</p>
            <p className="text-3xl font-bold text-blue-400" data-id="element-283">
              {formatCurrency(results.monthlyPayment)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-284">
            <p className="text-sm text-slate-400 mb-1" data-id="element-285">Total Interest</p>
            <p className="text-3xl font-bold text-cyan-400" data-id="element-286">
              {formatCurrency(results.totalInterest)}
            </p>
          </div>
          <div className="bg-white/5 p-6 rounded-2xl border border-white/10" data-id="element-287">
            <p className="text-sm text-slate-400 mb-1" data-id="element-288">Total Cost</p>
            <p className="text-3xl font-bold text-white" data-id="element-289">
              {formatCurrency(results.totalCost)}
            </p>
          </div>
        </div>

        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 h-[400px]" data-id="element-290">
          <h4 className="text-sm font-medium text-slate-400 mb-6" data-id="element-291">
            Amortization Schedule
          </h4>
          <ResponsiveContainer width="100%" height="100%" data-id="element-292">
            <AreaChart data={results.chartData} margin={{
            top: 10,
            right: 10,
            left: 0,
            bottom: 0
          }} data-id="element-293">
              <defs data-id="element-294">
                <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1" data-id="element-295">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-296" />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-297" />
                </linearGradient>
                <linearGradient id="colorInterest" x1="0" y1="0" x2="0" y2="1" data-id="element-298">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} data-id="element-299" />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-300" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} data-id="element-301" />
              <XAxis dataKey="year" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `Yr ${val}`} data-id="element-302" />
              <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={val => `$${val / 1000}k`} data-id="element-303" />
              <Tooltip contentStyle={{
              backgroundColor: '#0f172a',
              borderColor: '#1e293b',
              borderRadius: '8px',
              color: '#fff'
            }} formatter={(value: number) => formatCurrency(value)} labelFormatter={label => `Year ${label}`} data-id="element-304" />
              <Area type="monotone" dataKey="balance" name="Remaining Balance" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorBalance)" data-id="element-305" />
              <Area type="monotone" dataKey="interest" name="Cumulative Interest" stroke="#06b6d4" strokeWidth={2} fillOpacity={1} fill="url(#colorInterest)" data-id="element-306" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>;
}